import React, { useEffect, useState } from 'react';
import { queueApi } from '../services/api';
import { useEventStream } from '../hooks/useEventStream';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell } from './ui/table';

// bd-deepcontext-1zy7 — absolute URL, like the other fetch-based SSE consumers:
// in dev the frontend and backend sit on different ports.
const STREAM_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/queue/metrics-stream`;

interface QueueStats {
  queueLength: number;
  activeWorkers: number;
  busyWorkers: number;
  maxWorkers: number;
  processing: boolean;
  analyses?: Array<{
    analysisId: string;
    progress: string;
    pending: number;
    priority: number;
  }>;
}

interface SeparateQueueStats {
  userQueue: QueueStats;
  scheduledQueue: QueueStats;
  timestamp: string;
}

interface Metrics {
  pendingJobs: number;
  processingJobs: number;
  completedToday: number;
  failedToday: number;
  avgQueueTimeMs: number;
  avgProcessingTimeMs: number;
  tokensUsedToday: number;
  currentCapacityUsage: number;
  globalChunkQueue?: {
    queueLength: number;
    activeWorkers: number;
    activeAnalyses: number;
    analysisStats: Array<{
      analysisId: string;
      totalChunks: number;
      completedChunks: number;
      hasChunkInQueue: boolean;
      pendingChunks: number;
    }>;
  };
}

interface UserBreakdown {
  users: Array<{
    user_id: string | null;
    user_email: string | null;
    pending: number;
    processing: number;
    completed: number;
    failed: number;
    total: number;
  }>;
  total: {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
    total: number;
  };
}

const QueueMetrics: React.FC = () => {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [userBreakdown, setUserBreakdown] = useState<UserBreakdown | null>(null);
  const [queueStats, setQueueStats] = useState<SeparateQueueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // One REST fetch for first paint; everything after it arrives on the stream.
  useEffect(() => {
    fetchMetrics();
  }, []);

  // bd-deepcontext-1zy7 — the 10s poll of three admin endpoints (per open tab)
  // is gone. The backend now computes the payload once per tick and broadcasts
  // it, so a hidden tab costs nothing and updates land as soon as they exist.
  useEventStream(STREAM_URL, {
    queue_metrics: (data: any) => {
      if (!data) return;
      if (data.metrics) setMetrics(data.metrics);
      if (data.userBreakdown) setUserBreakdown(data.userBreakdown);
      if (data.queueStats) setQueueStats(data.queueStats);
      if (data.metrics || data.userBreakdown || data.queueStats) {
        setError(null);
        setLoading(false);
      }
    },
  });

  const fetchMetrics = async () => {
    try {
      const [metricsData, breakdownData, statsData] = await Promise.all([
        queueApi.getMetrics(),
        queueApi.getUserBreakdown(),
        queueApi.getQueueStats()
      ]);
      setMetrics(metricsData);
      setUserBreakdown(breakdownData);
      setQueueStats(statsData);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  if (loading && !metrics) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
            <p className="text-ink-muted mt-4">Loading metrics...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <div className="bg-risk-bg border border-risk rounded-md p-4">
        <p className="text-risk">{error}</p>
      </div>
    );
  }

  if (!metrics) return null;

  // Locked thresholds: OK <50, HIGH 50–80, CRITICAL >80 (bd deepcontext-2951).
  const capacityPct = metrics.currentCapacityUsage;
  const loadState: { label: string; variant: 'safe' | 'warn' | 'risk' } =
    capacityPct > 80 ? { label: 'CRITICAL', variant: 'risk' } :
    capacityPct >= 50 ? { label: 'HIGH', variant: 'warn' } :
    { label: 'OK', variant: 'safe' };

  return (
    <div>
      {/* Status KPI quartet — the loud, full-tint KPI variant reserved for
          status dashboards (bd deepcontext-2951): pending=nav-active,
          processing=warn, completed=safe, failed=risk. */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile tone="pend" label="Pending Jobs" value={formatNumber(metrics.pendingJobs)} />
        <KpiTile tone="proc" label="Processing" value={formatNumber(metrics.processingJobs)} />
        <KpiTile tone="done" label="Completed Today" value={formatNumber(metrics.completedToday)} />
        <KpiTile tone="fail" label="Failed Today" value={formatNumber(metrics.failedToday)} />
      </div>

      {/* Performance metrics as canvas cells inside a surface card. */}
      <Card className="mt-4">
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <PerfCell label="Average Queue Time" value={formatTime(metrics.avgQueueTimeMs)} />
            <PerfCell label="Average Processing Time" value={formatTime(metrics.avgProcessingTimeMs)} />
            <PerfCell label="Tokens Used Today" value={formatNumber(metrics.tokensUsedToday)} />
          </div>
        </CardContent>
      </Card>

      {/* Capacity Usage — always the brand-gradient bar, never a status
          color; the load state moves into a pill next to the title. */}
      <Card className="mt-4">
        <CardContent>
          <div className="flex items-baseline gap-2">
            <h3 className="text-sm font-extrabold text-ink">Capacity Usage</h3>
            <Badge variant={loadState.variant}>{loadState.label}</Badge>
            <span className="ml-auto text-lg font-extrabold text-ink">{capacityPct}%</span>
          </div>
          <Progress value={capacityPct} className="h-2.5 mt-2" />
          {metrics.globalChunkQueue && (
            <div className="mt-1.5 text-sm text-ink-muted">
              <span className="font-semibold">{metrics.globalChunkQueue.activeWorkers}</span> active chunks
              {' / '}
              <span className="font-semibold">{metrics.globalChunkQueue.queueLength}</span> in queue
              {metrics.globalChunkQueue.activeAnalyses > 0 && (
                <span className="ml-1">
                  ({metrics.globalChunkQueue.activeAnalyses} active {metrics.globalChunkQueue.activeAnalyses === 1 ? 'analysis' : 'analyses'})
                </span>
              )}
            </div>
          )}

          {queueStats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
              <QueueSystemBox title="User Queue" stats={queueStats.userQueue} />
              <QueueSystemBox title="Scheduled Queue" stats={queueStats.scheduledQueue} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Jobs by User — per-user breakdown, kept from the prior surface
          (not depicted in the page-lock illustration, which only fixes the
          KPI/perf/capacity anatomy above) but restyled onto the same tokens. */}
      {userBreakdown && userBreakdown.users.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-extrabold text-ink mb-2">Jobs by User (Last 24 Hours)</h3>
          {/* Table-card spec (deepcontext-c0p7.8): shared shell metrics
              (header px-4 py-3 uppercase tracking-wide; cells px-4 py-3;
              hover:bg-ghost-bg rows) + card chrome. No pagination here — the
              breakdown covers the full 24h window, so there's no footer to
              relocate into the card. */}
          <div className="overflow-x-auto rounded-2xl border border-line bg-surface shadow-sm" data-testid="jobs-by-user-panel">
            <Table>
              <TableHead>
                <TableRow className="hover:bg-transparent">
                  <TableHeaderCell>User</TableHeaderCell>
                  <TableHeaderCell className="text-center">Pending</TableHeaderCell>
                  <TableHeaderCell className="text-center">Processing</TableHeaderCell>
                  <TableHeaderCell className="text-center">Completed</TableHeaderCell>
                  <TableHeaderCell className="text-center">Failed</TableHeaderCell>
                  <TableHeaderCell className="text-center">Total</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userBreakdown.users.map((user, index) => (
                  <TableRow key={user.user_id || `user-${index}`}>
                    <TableCell className="font-medium">{user.user_email || 'Unknown User'}</TableCell>
                    <UserCountCell count={user.pending} variant="default" />
                    <UserCountCell count={user.processing} variant="warn" />
                    <UserCountCell count={user.completed} variant="safe" />
                    <UserCountCell count={user.failed} variant="risk" />
                    <TableCell className="text-center font-medium">{user.total}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-ghost-bg font-medium hover:bg-ghost-bg">
                  <TableCell>Total</TableCell>
                  <UserCountCell count={userBreakdown.total.pending} variant="default" />
                  <UserCountCell count={userBreakdown.total.processing} variant="warn" />
                  <UserCountCell count={userBreakdown.total.completed} variant="safe" />
                  <UserCountCell count={userBreakdown.total.failed} variant="risk" />
                  <TableCell className="text-center">{userBreakdown.total.total}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Live-stream indicator — updates are pushed, not polled. A static
          dot, not a looping animation (bd deepcontext-2951). */}
      <div className="mt-4 text-center text-xs text-ink-muted">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-safe" />
          Live — updates streamed from the server
        </span>
      </div>
    </div>
  );
};

const KPI_TONES: Record<'pend' | 'proc' | 'done' | 'fail', string> = {
  pend: 'bg-nav-active text-accent-active',
  proc: 'bg-warn-bg text-warn',
  done: 'bg-safe-bg text-safe',
  fail: 'bg-risk-bg text-risk',
};

function KpiTile({ tone, label, value }: { tone: 'pend' | 'proc' | 'done' | 'fail'; label: string; value: string }) {
  return (
    <div className={`rounded-2xl p-4 ${KPI_TONES[tone]}`}>
      <p className="text-xs font-bold uppercase tracking-wide">{label}</p>
      <p className="text-xl font-extrabold mt-1">{value}</p>
    </div>
  );
}

function PerfCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-canvas rounded-xl px-4 py-3">
      <p className="text-xs font-bold uppercase tracking-wide text-ink-muted">{label}</p>
      <p className="text-base font-extrabold text-ink mt-1">{value}</p>
    </div>
  );
}

function QueueSystemBox({ title, stats }: { title: string; stats: QueueStats }) {
  const utilizationPct = stats.maxWorkers > 0 ? Math.round((stats.busyWorkers / stats.maxWorkers) * 100) : 0;
  return (
    <div className="bg-canvas rounded-xl px-4 py-3">
      <h4 className="text-sm font-bold text-ink mb-1.5">{title}</h4>
      <div className="space-y-1 text-sm text-ink-muted">
        <div className="flex justify-between"><span>Queue Length</span><span className="font-semibold text-ink">{stats.queueLength}</span></div>
        <div className="flex justify-between"><span>Active Workers</span><span className="font-semibold text-ink">{stats.activeWorkers} / {stats.maxWorkers}</span></div>
        <div className="flex justify-between"><span>Busy Workers</span><span className="font-semibold text-ink">{stats.busyWorkers}</span></div>
        <div className="flex justify-between">
          <span>Status</span>
          <span className={`font-semibold ${stats.processing ? 'text-safe' : 'text-ink-muted'}`}>{stats.processing ? 'Processing' : 'Idle'}</span>
        </div>
        <div className="mt-2">
          <div className="flex justify-between text-sm mb-1"><span>Worker Utilization</span><span>{utilizationPct}%</span></div>
          <Progress value={utilizationPct} className="h-1.5" />
        </div>
        {stats.analyses && stats.analyses.length > 0 && (
          <div className="mt-2 pt-2 border-t border-line">
            <p className="text-sm mb-1">Active Analyses:</p>
            <div className="space-y-0.5 max-h-24 overflow-y-auto">
              {stats.analyses.map((analysis) => (
                <div key={analysis.analysisId} className="text-sm font-mono">
                  {analysis.analysisId.substring(0, 8)}… <span className="ml-1">{analysis.progress}</span> <span className="ml-1">P{analysis.priority}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function UserCountCell({ count, variant }: { count: number; variant: 'default' | 'warn' | 'safe' | 'risk' }) {
  return (
    <TableCell className="text-center">
      {count > 0 ? <Badge variant={variant}>{count}</Badge> : <span className="text-ink-muted">0</span>}
    </TableCell>
  );
}

export default QueueMetrics;
